import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Numeric } from '@/components/ui/numeric'
import { cn } from '@/lib/utils'
import { PropertyTaxonomyFields } from '@/components/listings/taxonomy/PropertyTaxonomyFields'
import type { TaxonomyFormSlice } from '@/lib/propertyTaxonomy'
import type { ComposerFormState } from './types'

export interface StepPropertyDetailsProps {
  form: ComposerFormState
  errors: Record<string, string>
  onChange: <K extends keyof ComposerFormState>(key: K, value: ComposerFormState[K]) => void
}

export function StepPropertyDetails({ form, errors, onChange }: StepPropertyDetailsProps) {
  const showLot = form.property_type === 'villa'
  const taxonomySlice: TaxonomyFormSlice = {
    category: form.category,
    property_type: form.property_type,
    property_subtype: form.property_subtype,
    other_label: form.other_label,
    plot_area: form.plot_area,
    permissible_buildup_area: form.permissible_buildup_area,
    site_coverage_percent: form.site_coverage_percent,
    permitted_floors: form.permitted_floors,
    zone_code: form.zone_code,
    attribute_values: form.attribute_values,
  }

  function applyTaxonomy(next: TaxonomyFormSlice) {
    onChange('category', next.category)
    onChange('property_type', next.property_type)
    onChange('property_subtype', next.property_subtype)
    onChange('other_label', next.other_label)
    onChange('plot_area', next.plot_area)
    onChange('permissible_buildup_area', next.permissible_buildup_area)
    onChange('site_coverage_percent', next.site_coverage_percent)
    onChange('permitted_floors', next.permitted_floors)
    onChange('zone_code', next.zone_code)
    onChange('attribute_values', next.attribute_values)
  }

  return (
    <div className="space-y-[var(--lc-space-xl)]">
      <PropertyTaxonomyFields
        selection={taxonomySlice}
        onChange={applyTaxonomy}
        idPrefix="composer"
        appearance="composer"
        typeError={errors.property_type}
      />

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="composer-beds">Bedrooms</Label>
          <Input
            id="composer-beds"
            inputMode="numeric"
            value={form.bedrooms}
            onChange={(e) => onChange('bedrooms', e.target.value.replace(/\D/g, ''))}
            className="font-[family-name:var(--lc-font-mono)]"
          />
        </div>
        <div>
          <Label htmlFor="composer-baths">Bathrooms</Label>
          <Input
            id="composer-baths"
            inputMode="numeric"
            value={form.bathrooms}
            onChange={(e) => onChange('bathrooms', e.target.value.replace(/\D/g, ''))}
            className="font-[family-name:var(--lc-font-mono)]"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="composer-area-size">Floor area</Label>
        <div className="flex gap-2">
          <Input
            id="composer-area-size"
            inputMode="decimal"
            value={form.area}
            onChange={(e) => onChange('area', e.target.value.replace(/[^\d.]/g, ''))}
            className="flex-1 font-[family-name:var(--lc-font-mono)]"
          />
          <div
            role="group"
            aria-label="Area unit"
            className="inline-flex rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] p-0.5"
          >
            {(['sqft', 'sqm'] as const).map((u) => (
              <button
                key={u}
                type="button"
                aria-pressed={form.area_unit === u}
                onClick={() => onChange('area_unit', u)}
                className={cn(
                  'min-h-tap rounded-[var(--lc-radius-md)] px-3 text-[length:var(--lc-type-caption)]',
                  form.area_unit === u
                    ? 'bg-[var(--lc-action-primary)] text-[var(--lc-action-primary-text)]'
                    : 'bg-[var(--lc-surface-sunken)] text-[var(--lc-text-secondary)]',
                )}
              >
                <Numeric>{u}</Numeric>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="composer-year">Year built (optional)</Label>
          <Input
            id="composer-year"
            inputMode="numeric"
            value={form.year_built}
            onChange={(e) => onChange('year_built', e.target.value.replace(/\D/g, ''))}
            className="font-[family-name:var(--lc-font-mono)]"
          />
        </div>
        {form.category !== 'land' && (
          <div>
            <Label htmlFor="composer-floor">Floor number (optional)</Label>
            <Input
              id="composer-floor"
              inputMode="numeric"
              value={form.floor}
              onChange={(e) => onChange('floor', e.target.value.replace(/\D/g, ''))}
              className="font-[family-name:var(--lc-font-mono)]"
            />
          </div>
        )}
      </div>

      {showLot && (
        <div>
          <Label htmlFor="composer-lot">Lot / plot size (optional)</Label>
          <Input
            id="composer-lot"
            inputMode="decimal"
            value={form.lot_size}
            onChange={(e) => onChange('lot_size', e.target.value.replace(/[^\d.]/g, ''))}
            className="font-[family-name:var(--lc-font-mono)]"
          />
        </div>
      )}

    </div>
  )
}
